import { Button, ConfirmDialog, InlineButton, Loading } from "@vellum/ui";
import type {
  DashboardSubscriptionSchema,
  InternalCompanySchema,
  InternalFacilitySchema,
  SubscriptionType,
} from "@vellum/vellum-api";
import { useCallback, useEffect, useState } from "react";

import { vellumApiSdk } from "@vellum/micro-app-auth";

import { formatSendHourCT } from "./constants";
import { SendConfirmDialog } from "./SendConfirmDialog";
import { SubscriptionDialog } from "./SubscriptionDialog";

interface SubscriptionsSectionProps {
  typ: SubscriptionType;
  company: InternalCompanySchema;
  /** Required when typ is Daily. */
  facility?: InternalFacilitySchema | null;
  /** Metabase question the subscription's emailed CSV is rendered from. */
  metabaseReportId: number;
  onSubscriptionChange?: (
    subscription: DashboardSubscriptionSchema | null,
  ) => void;
  /** Called after an on-demand send so the send history can refresh. */
  onSent?: () => void;
}

export function SubscriptionsSection({
  typ,
  company,
  facility,
  metabaseReportId,
  onSubscriptionChange,
  onSent,
}: SubscriptionsSectionProps) {
  const [subscription, setSubscription] =
    useState<DashboardSubscriptionSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  /**
   * Whether a loaded subscription is this report's.
   *
   * `dashboard_subscriptions` is keyed on (typ, company_id, facility_id) with
   * no reference to a report, so a lookup for this scope can return a row that
   * a different report created. Saving over it would silently repoint that
   * report's emails at this report's question, so a foreign row is shown as
   * read-only until subscriptions carry a report id of their own.
   */
  const belongsToThisReport = useCallback(
    (candidate: DashboardSubscriptionSchema) =>
      candidate.metabase_report_id === String(metabaseReportId),
    [metabaseReportId],
  );

  const updateSubscription = useCallback(
    (next: DashboardSubscriptionSchema | null) => {
      setSubscription(next);
      // The send history below belongs to this report only if the
      // subscription does.
      onSubscriptionChange?.(next && belongsToThisReport(next) ? next : null);
    },
    [onSubscriptionChange, belongsToThisReport],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    vellumApiSdk
      .listSubscriptions({
        query: {
          typ,
          company_id: company.id,
          facility_id: facility?.id ?? null,
        },
      })
      .then((res) => {
        if (!active) {
          return;
        }
        // The generated SDK resolves with an `error` rather than rejecting, so
        // a rejected lookup is a value to check — treating it as "no
        // subscription" is what made this section fail silently.
        if (res.error !== undefined || !res.data) {
          setError("We could not load this report's subscription.");
          updateSubscription(null);
          return;
        }
        updateSubscription(res.data[0] ?? null);
      })
      .catch(() => {
        if (active) {
          setError("Something went wrong loading subscriptions.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typ, company.id, facility?.id]);

  const foreignSubscription =
    subscription !== null && !belongsToThisReport(subscription);

  const handleDelete = async () => {
    if (!subscription) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await vellumApiSdk.deleteSubscription({
        path: { subscription_id: subscription.id },
      });
      if (res.error) {
        setDeleteError(
          res.error.detail?.map((e) => e.msg).join(", ") ??
            "Failed to delete subscription",
        );
      } else {
        updateSubscription(null);
      }
    } catch {
      setDeleteError("Failed to delete subscription");
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <div className="border-b border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold ">Subscription</h2>
        </div>
        {/* On error, the message below stands alone: offering "Create" while
            the current state is unknown would just be a guess. A subscription
            another report owns is read-only, because every control here would
            act on that report's row — Save would repoint its emails at this
            report's question, and Create would 400 as a duplicate. */}
        {loading ? (
          <Loading />
        ) : error ? null : foreignSubscription ? (
          <p className="max-w-md text-right text-sm text-gray-500">
            Another report already sends the {typ.toLowerCase()} email for this{" "}
            {facility ? "facility" : "company"}. Manage it from that report —
            editing it here would change where its emails come from.
          </p>
        ) : subscription ? (
          <div className="flex items-center gap-3">
            <div className="text-right text-sm text-gray-700">
              <span className="font-medium">{subscription.typ}</span>
              {" · "}
              {formatSendHourCT(subscription.send_hour)}
              {" · "}
              <span className="text-gray-500">
                {subscription.to_emails.join(", ")}
              </span>
            </div>
            <InlineButton
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setSendDialogOpen(true)}
            >
              <span className="iconoir-send text-xl" aria-hidden="true" />
            </InlineButton>
            <InlineButton
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setDialogOpen(true)}
            >
              <span
                className="iconoir-edit-pencil text-xl"
                aria-hidden="true"
              />
            </InlineButton>
            <InlineButton
              className="text-gray-500 hover:text-gray-700"
              disabled={deleting}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <span className="iconoir-trash text-xl" aria-hidden="true" />
            </InlineButton>
          </div>
        ) : (
          <Button type="button" onClick={() => setDialogOpen(true)}>
            Create Subscription
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-error">{error}</p>}
      {deleteError && <p className="mt-2 text-sm text-error">{deleteError}</p>}
      {dialogOpen && (
        <SubscriptionDialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSaved={updateSubscription}
          typ={typ}
          companyId={company.id}
          facilityId={facility?.id}
          metabaseReportId={metabaseReportId}
          subscription={subscription}
        />
      )}
      {sendDialogOpen && subscription && (
        <SendConfirmDialog
          isOpen={sendDialogOpen}
          onClose={() => setSendDialogOpen(false)}
          onSent={onSent}
          subscription={subscription}
        />
      )}
      <ConfirmDialog
        variant="delete"
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Are you sure you want to delete this subscription?"
      />
    </div>
  );
}
